import {
  ChangeEventHandler,
  ClipboardEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
} from "react";
import styled, { css } from "styled-components";
import { TextBox as SundayV3TextBox } from "@sunday/component";
import { TextBox as SundayTextBox } from "sunday-component";

const legacyTextBoxStyles = css`
  &&& {
    & {
      input {
        color: var(--ui-form-input-text-activated-fields_text-color);
        caret-color: var(--ui-form-input-text-activated-fields_text-color);
        background-color: var(--ui-form-input-text-activated-background-color);
        ::placeholder {
          color: var(--ui-form-input-text-activated-placeholder_text-color);
          background-color: var(
            --ui-form-input-text-activated-background-color
          );
        }
      }
      background-color: var(--ui-form-input-text-activated-background-color);
      border-color: var(--ui-form-input-text-activated-border-color);
    }

    &.sunday-component-input-affix-wrapper-focused {
      input {
        background-color: var(--ui-form-input-text-focused-background-color);
        ::placeholder {
          color: var(--ui-form-input-text-focused-placeholder_text-color);
          background-color: var(--ui-form-input-text-focused-background-color);
        }
      }
      background-color: var(--ui-form-input-text-focused-background-color);
      border-color: var(--ui-form-input-text-focused-border-color);
      box-shadow: 0 0 0 2px var(--ui-form-input-text-focused-border-shadow);
    }

    &:not(.sunday-component-input-affix-wrapper-disabled) {
      :hover {
        input {
          background-color: var(--ui-form-input-text-hover-background-color);
          ::placeholder {
            color: var(--ui-form-input-text-hover-placeholder_text-color);
            background-color: var(--ui-form-input-text-hover-background-color);
          }
        }
        background-color: var(--ui-form-input-text-hover-background-color);
        border-color: var(--ui-form-input-text-hover-border-color);
      }
    }

    &.sunday-component-input-affix-wrapper-disabled {
      input {
        color: var(--ui-form-input-text-disabled-fields_text-color);
        background-color: var(--ui-form-input-text-disabled-background-color);
        ::placeholder {
          color: var(--ui-form-input-text-disabled-placeholder_text-color);
          background-color: var(--ui-form-input-text-disabled-background-color);
        }
      }
      background-color: var(--ui-form-input-text-disabled-background-color);
      border-color: var(--ui-form-input-text-disabled-border-color);
    }
  }
`;

const iceCreamTextBoxStyles = css<{ $appearance?: "ice-cream" }>`
  &&& {
    font-family: "Noto Sans Thai", sans-serif;
    height: 48px;

    input {
      font-family: "Noto Sans Thai", sans-serif;
      font-size: 15px;
    }

    ${({ $appearance }) =>
      $appearance === "ice-cream" &&
      css`
        --textbox-bg-color-disabled: var(--color-surface-tertiary, #ecedef);
        --textbox-border-color: var(--color-stroke-default, #d9dbde);
        --textbox-border-color-focus: var(--color-stroke-ci, #435dff);
        --textbox-border-radius: 8px;
        --textbox-caret-color: var(--color-stroke-ci, #435dff);
        --textbox-font-family: var(--typescale-font-base-th, "Noto Sans Thai"),
          sans-serif;
        --textbox-font-size: var(--typescale-size-b-2, 15px);
        --textbox-font-weight: var(--typescale-weight-base-regular, 400);
        --textbox-line-height: var(--typescale-line-height-b-2, 24px);
        --textbox-padding: 0 12px;
        --textbox-placeholder-color: var(--color-text-disabled, #9fa6ad);
        --textbox-value-color: var(--color-text-primary, #202123);

        width: 100%;
        height: 48px;
        padding: 0 12px;
        border-color: var(--color-stroke-default, #d9dbde);
        border-radius: 8px;
        background: var(--color-surface-primary, #ffffff);
        color: var(--color-text-primary, #202123);
        font-size: var(--typescale-size-b-2, 15px);
        font-weight: var(--typescale-weight-base-regular, 400);
        line-height: var(--typescale-line-height-b-2, 24px);

        &.ant-input-affix-wrapper {
          display: inline-flex;
          align-items: center;
        }

        &.ant-input-affix-wrapper > input.ant-input {
          height: 46px;
          padding: 0;
          border: 0;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
        }

        &.ant-input:not(.ant-input-disabled):hover,
        &.ant-input:not(.ant-input-disabled):focus,
        &.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover,
        &.ant-input-affix-wrapper-focused {
          border-color: var(--color-stroke-ci, #435dff);
          box-shadow: none;
        }

        &::placeholder,
        input::placeholder {
          color: var(--color-text-disabled, #9fa6ad);
          -webkit-text-fill-color: var(--color-text-disabled, #9fa6ad);
          opacity: 1;
        }
      `}
  }
`;

export const CustomTextBox = styled(SundayTextBox)<{
  $appearance?: "ice-cream";
}>`
  ${({ $appearance }) =>
    $appearance === "ice-cream" ? iceCreamTextBoxStyles : legacyTextBoxStyles}
`;

const disabledNumberScroll = (event) => {
  event.currentTarget.blur();
};

export type TextBoxProp = {
  dataTestId?: string;
  size?: "large" | "middle" | "small";
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onClick?: MouseEventHandler<HTMLInputElement>;
  onWheel?: MouseEventHandler<HTMLInputElement>;
  onPaste?: ClipboardEventHandler<HTMLInputElement>;
  onCopy?: ClipboardEventHandler<HTMLInputElement>;
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
  type?: string;
  suffix?: React.ReactNode;
  value?: string;
  maxLength?: number;
  appearance?: "ice-cream";
};

const TextBox = ({
  dataTestId = "",
  size = "large",
  placeholder = "",
  defaultValue = null,
  disabled = false,
  onChange = () => {},
  onClick = () => {},
  onWheel = () => {},
  onPaste = () => {},
  onCopy = () => {},
  suffix = <></>,
  value,
  maxLength = undefined,
  appearance,
  ...prop
}: TextBoxProp) => (
  <CustomTextBox
    as={appearance === "ice-cream" ? SundayV3TextBox : undefined}
    {...(appearance === "ice-cream" ? {} : { size })}
    $appearance={appearance}
    data-testid={dataTestId}
    data-gtm-input-code={dataTestId}
    placeholder={placeholder}
    defaultValue={defaultValue}
    onChange={onChange}
    onClick={onClick}
    onWheel={prop?.type === "number" ? disabledNumberScroll : () => {}}
    disabled={disabled}
    suffix={suffix}
    value={value}
    onPaste={onPaste}
    onCopy={onCopy}
    maxLength={maxLength}
    {...prop}
  />
);

export default TextBox;
